import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { updateApiKey, getRoutingConfig } from '../utils/routing-config-manager.js';
import debug from '@commons-shared/debug';
import { config } from '../config/environment';

export const useConfigForm = (language) => {
  const [apiKeysLoaded, setApiKeysLoaded] = useState(false);
  const [initialApiKeys, setInitialApiKeys] = useState({
    textAiApiKey: '',
    visionAiApiKey: '',
    translationAiApiKey: ''
  });

  const form = useForm({
    defaultValues: {
      // UI
      uiLanguage: language,
      // IAs - La langue de travail IA sera chargée async depuis routing_config
      aiWorkingLanguage: '',
      textAiApiKey: '', // Sera chargé de manière asynchrone depuis routing_config
      textAiModel: '', // Sera chargé de manière asynchrone depuis routing_config
      visionAiApiKey: '', // Sera chargé de manière asynchrone depuis routing_config
      visionAiModel: '', // Sera chargé de manière asynchrone depuis routing_config
      translationAiApiKey: '', // Sera chargé de manière asynchrone depuis routing_config
      translationAiModel: '', // Sera chargé de manière asynchrone depuis routing_config
    }
  });

  // Fonctions utilitaires pour le stockage des clés API dans routing_config
  // Utilisez useCallback pour éviter les re-créations inutiles
  const getEncryptedApiKey = useCallback(async (keyName) => {
    try {
      // Récupérer la clé depuis routing_config basé sur le nom de la clé
      // Exemple: 'text_ai_api_key' -> routing_config.text['X-API-Key']
      const routingConfig = await getRoutingConfig();
      
      // Map keyName (ex: 'text_ai_api_key') vers le type de section (ex: 'text')
      const sectionMap = {
        'text_ai_api_key': 'text',
        'vision_ai_api_key': 'vision',
        'translation_ai_api_key': 'translation',
        'wa_api_key': 'wa'
      };
      
      const section = sectionMap[keyName];
      if (!section || !routingConfig[section] || !routingConfig[section]['X-API-Key']) {
        debug.warn(config, `Clé ${keyName} non trouvée dans routing_config`);
        return '';
      }
      
      // La clé est stockée chiffrée dans routing_config, on la retourne comme est
      return routingConfig[section]['X-API-Key'] || '';
    } catch (error) {
      debug.error(config, `Erreur lors de la récupération de ${keyName}:`, error);
      return '';
    }
  }, []);

  const setEncryptedApiKey = useCallback(async (keyName, value) => {
    try {
      // Map keyName (ex: 'text_ai_api_key') vers le type de section (ex: 'text')
      const sectionMap = {
        'text_ai_api_key': 'text',
        'vision_ai_api_key': 'vision',
        'translation_ai_api_key': 'translation',
        'wa_api_key': 'wa'
      };
      
      const section = sectionMap[keyName];
      if (!section) {
        debug.error(config, `Clé inconnue: ${keyName}`);
        return false;
      }
      
      if (!value || value.trim() === '') {
        // Supprimer la clé en la mettant à null
        await updateApiKey(section, null);
        debug.log(config, `Clé ${keyName} (section ${section}) supprimée`);
        return true;
      }
      
      // Sauvegarder dans routing_config avec la bonne section
      await updateApiKey(section, value);
      debug.log(config, `Clé ${keyName} (section ${section}) sauvegardée dans routing_config`);
      return true;
    } catch (error) {
      debug.error(config, `Erreur lors de la sauvegarde de ${keyName}:`, error);
      return false;
    }
  }, []);

  return {
    form,
    apiKeysLoaded,
    setApiKeysLoaded,
    initialApiKeys,
    setInitialApiKeys,
    getEncryptedApiKey,
    setEncryptedApiKey
  };
};
